import { useMemo } from 'react'

import { Box, Button, ScrollContainer, Text } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import {
  type SelectedCompetency,
  toSelectedCompetency,
} from 'modules/competencies/hooks/useCompetencyFinder'
import {
  type CompetencySuggestionTarget,
  canSuggestForTarget,
  useFactorialCompetenciesSuggestions,
} from 'modules/competencies/hooks/useFactorialCompetenciesSuggestions'

import { SuggestionItem } from './SuggestionItem'
import { SuggestionSkeletons } from './SuggestionSkeleton'

type Props = {
  target: CompetencySuggestionTarget
  initialUuids: string[]
  excludeUuids: string[]
  onAdd: (competency: SelectedCompetency) => void
}

export const Suggestions = ({ target, initialUuids, excludeUuids, onAdd }: Props) => {
  const {
    suggestions,
    loading,
    loadingMore,
    canSuggestMore,
    loadingQuantity,
    fetchMoreSuggestions,
  } = useFactorialCompetenciesSuggestions({
    target,
    initialUuids: initialUuids,
  })

  const { competencies, allCombinedUuids } = useMemo(() => {
    const excludeSet = new Set(excludeUuids)
    const filteredCompetencies = suggestions.filter(({ uuid }) => !excludeSet.has(uuid))

    const combinedUuids = [
      ...new Set([
        ...initialUuids,
        ...excludeUuids,
        ...filteredCompetencies.map(({ uuid }) => uuid),
      ]),
    ]

    return {
      competencies: filteredCompetencies,
      allCombinedUuids: combinedUuids,
    }
  }, [suggestions, excludeUuids, initialUuids])

  if (!canSuggestForTarget(target)) {
    return null
  }

  if (!loading && !loadingMore && competencies.length === 0 && !canSuggestMore) {
    return null
  }

  return (
    <Box gap="s8" flexDirection="column">
      <Text.Normal size="200" weight="medium" color="grey1000">
        {i18n.t('competencies_v3.competencies_finder.suggestions_for', {
          title: target.title,
        })}
      </Text.Normal>
      <ScrollContainer maxHeight="s400">
        <Box gap="s8" flexDirection="column">
          <Box flexDirection="column" gap="s8">
            {loading && (
              <SuggestionSkeletons quantity={loadingQuantity} identifier="initial-suggestions" />
            )}
            {!loading &&
              competencies.map((competency) => (
                <SuggestionItem
                  key={`suggestion-${competency.uuid}`}
                  competency={competency}
                  onAdd={() => onAdd(toSelectedCompetency(competency, 'suggestions'))}
                />
              ))}
          </Box>
          {loadingMore && (
            <SuggestionSkeletons quantity={loadingQuantity} identifier="more-suggestions" />
          )}
          {canSuggestMore && (
            <Button onClick={() => fetchMoreSuggestions(allCombinedUuids)} type="secondary">
              {i18n.t('competencies_v3.competencies_finder.load_more')}
            </Button>
          )}
        </Box>
      </ScrollContainer>
    </Box>
  )
}
